import React from 'react';
import {
    Inventory2 as Inventory2Icon,
    Warehouse as WarehouseIcon,
    AddShoppingCart as AddShoppingCartIcon,
    People as PeopleIcon,
    ShoppingBasket as ShoppingBasketIcon,
    Storefront as StorefrontIcon,
    PersonAdd as PersonAddIcon,
    PointOfSale as PointOfSaleIcon,
    Logout,
    AddBox as AddBoxIcon,
    ListAlt as ListAltIcon,
    Category as CategoryIcon,
    ShoppingCart as ShoppingCartIcon,
    Dashboard as DashboardIcon,
    TrendingUp as TrendingUpIcon,
    AccountBalance as AccountBalanceIcon,
    ShowChart as ShowChartIcon,
    ImportExport as ImportExportIcon,
    Business as BusinessIcon,
    Print as PrintIcon,
    LocalShipping as LocalShippingIcon,
    Receipt as ReceiptIcon,
    AccountTree as AccountTreeIcon,
    Assignment as AssignmentIcon,
    RequestQuote as RequestQuoteIcon,
    Payments as PaymentsIcon,
    FactCheck as FactCheckIcon,
    Undo as UndoIcon,
    Tune as TuneIcon,
} from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { TAX_CONFIG_PATH, ZATCA_INVOICES_PATH } from "../utilities/taxConfigurationUtils";
import { MODULE_SETTINGS_PATH } from "../utilities/moduleConfigurationUtils";
import { normalizeUserRole } from "../utilities/userRoleUtils";

export const getMenuConfig = (userRole, appType, bussinessType, cartlength) => {

    const normalizedRole = normalizeUserRole(userRole);
    
    // Normalized Role Checks
    const isAdmin = normalizedRole === "Admin" || normalizedRole === "ADMIN";
    const isManager = normalizedRole === "Manager" || normalizedRole === "MANAGER";
    const isAgent = normalizedRole === "Agent" || normalizedRole === "AGENT";

    // Normalize appType (remove trailing dash)
    const cleanAppType = appType ? appType.replace(/-$/, '') : appType;

    // Business Logic Checks
    const isLogisticsHOMS = bussinessType?.toUpperCase() === "LOGISTIC" && cleanAppType === "HOMS";
    let appKey = isLogisticsHOMS ? "HOMS-LOGISTICS" : cleanAppType;

    // Define Menu Structure with Data Import/Export added to all apps
    const menuItems = {
        "HPOS": {
            admin: [
                {
                    section: "POINT OF SALE",
                    items: [
                        { name: "POS", icon: <PointOfSaleIcon />, path: "/Pos", badge: cartlength > 0 ? cartlength : null },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                    ],
                },
                {
                    section: "PRODUCT CATALOG",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                    ],
                },
                {
                    section: "STOCK OPERATIONS",
                    items: [
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                        { name: "Tax Configuration", icon: <ReceiptLongIcon />, path: TAX_CONFIG_PATH },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            manager: [
                {
                    section: "OPERATIONS",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Create Product", icon: <AddBoxIcon />, path: "/ProductForm" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                        { name: "Tax Configuration", icon: <ReceiptLongIcon />, path: TAX_CONFIG_PATH },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            agent: [
                {
                    section: "SALES",
                    items: [
                        { name: "POS", icon: <PointOfSaleIcon />, path: "/POS", badge: cartlength > 0 ? cartlength : null },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HCRM": {
            admin: [
                {
                    section: "CUSTOMER MANAGEMENT",
                    items: [
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                        { name: "Add Customer", icon: <PersonAddIcon />, path: "/customer" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HPRMS": {
            admin: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "PROCUREMENT",
                    items: [
                        { name: "Purchase Requests", icon: <AssignmentIcon />, path: "/PurchaseRequests" },
                        { name: "RFQs", icon: <RequestQuoteIcon />, path: "/RFQs" },
                        { name: "Purchase Orders", icon: <ListAltIcon />, path: "/Purchase Orders" },
                        { name: "Vendor Bills", icon: <ReceiptIcon />, path: "/VendorBills" },
                        { name: "Vendor Payments", icon: <PaymentsIcon />, path: "/VendorPayments" },
                        { name: "Vendors", icon: <StorefrontIcon />, path: "/Vendors" },
                    ],
                },
                {
                    section: "PRODUCT CATALOG",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                    ],
                },
                {
                    section: "STOCK OPERATIONS",
                    items: [
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                        { name: "Goods Receipts", icon: <ReceiptLongIcon />, path: "/GoodsReceipts" },
                        { name: "Quality Inspections", icon: <FactCheckIcon />, path: "/QualityInspections" },
                        { name: "Purchase Returns", icon: <UndoIcon />, path: "/PurchaseReturns" },
                        { name: "GRN (legacy)", icon: <ReceiptLongIcon />, path: "/grn" },
                    ],
                },
                {
                    section: "REPORTS",
                    items: [
                        { name: "Purchase Overview", icon: <ShoppingCartIcon />, path: "/reports/purchases/overview" },
                        { name: "Inventory Overview", icon: <WarehouseIcon />, path: "/reports/inventory/overview" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                        { name: "Module Configuration", icon: <TuneIcon />, path: MODULE_SETTINGS_PATH },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HPiMS": {
            admin: [
                {
                    section: "PRODUCT CATALOG",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                    ],
                },
                {
                    section: "STOCK OPERATIONS",
                    items: [
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HOMS": {
            admin: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "SALES & ORDERS",
                    items: [
                        { name: "Sales Pipeline", icon: <DashboardIcon />, path: "/SalesPipeline" },
                        { name: "Quotations", icon: <ReceiptLongIcon />, path: "/Quotations" },
                        { name: "Sales Orders", icon: <ListAltIcon />, path: "/Sales Order" },
                        { name: "Deliveries", icon: <LocalShippingIcon />, path: "/Deliveries" },
                        { name: "Invoices", icon: <ReceiptIcon />, path: "/Invoices" },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                    ],
                },
                {
                    section: "PRODUCT CATALOG",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                    ],
                },
                {
                    section: "STOCK OPERATIONS",
                    items: [
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                    ],
                },
                {
                    section: "REPORTS",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Top Products", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                        { name: "ZATCA Invoices", icon: <ReceiptLongIcon />, path: ZATCA_INVOICES_PATH },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                        { name: "Business Settings", icon: <BusinessIcon />, path: "/configuration/business-settings" },
                        { name: "Tax Configuration", icon: <ReceiptLongIcon />, path: TAX_CONFIG_PATH },
                        { name: "Printer Server Settings", icon: <PrintIcon />, path: "/configuration/printer-settings" },
                        { name: "Module Configuration", icon: <TuneIcon />, path: MODULE_SETTINGS_PATH },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            manager: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "OPERATIONS",
                    items: [
                        { name: "Sales Orders", icon: <ListAltIcon />, path: "/Sales Order" },
                        { name: "Sales Journey", icon: <AccountTreeIcon />, path: "/SalesJourney" },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                    ],
                },
                {
                    section: "REPORTS",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Top Products", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                        { name: "ZATCA Invoices", icon: <ReceiptLongIcon />, path: ZATCA_INVOICES_PATH },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                        { name: "Business Settings", icon: <BusinessIcon />, path: "/configuration/business-settings" },
                        { name: "Tax Configuration", icon: <ReceiptLongIcon />, path: TAX_CONFIG_PATH },
                        { name: "Printer Server Settings", icon: <PrintIcon />, path: "/configuration/printer-settings" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            agent: [
                {
                    section: "SALES",
                    items: [
                        { name: "POS", icon: <PointOfSaleIcon />, path: "/POS", badge: cartlength > 0 ? cartlength : null },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HOMS-LOGISTICS": {
            admin: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "SALES & ORDERS",
                    items: [
                        { name: "Sales Orders", icon: <ListAltIcon />, path: "/Sales Order" },
                        { name: "Sales Journey", icon: <AccountTreeIcon />, path: "/SalesJourney" },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                    ],
                },
                {
                    section: "PURCHASE & VENDORS",
                    items: [
                        { name: "Purchase Orders", icon: <ShoppingBasketIcon />, path: "/Purchase Orders" },
                        { name: "Vendors", icon: <StorefrontIcon />, path: "/Vendors" },
                    ],
                },
                {
                    section: "CORE",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Categories", icon: <CategoryIcon />, path: "/Categories" },
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                    ],
                },
                {
                    section: "REPORTS",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Top Products", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                        { name: "ZATCA Invoices", icon: <ReceiptLongIcon />, path: ZATCA_INVOICES_PATH },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            manager: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "OPERATIONS",
                    items: [
                        { name: "Products", icon: <Inventory2Icon />, path: "/Products" },
                        { name: "Inventory", icon: <WarehouseIcon />, path: "/inventory" },
                        { name: "Sales Orders", icon: <ListAltIcon />, path: "/Sales Order" },
                        { name: "Customers", icon: <PeopleIcon />, path: "/Customers" },
                    ],
                },
                {
                    section: "REPORTS",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Top Products", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            agent: [
                {
                    section: "SALES",
                    items: [
                        { name: "POS", icon: <PointOfSaleIcon />, path: "/POS", badge: cartlength > 0 ? cartlength : null },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HRMS": {
            admin: [
                {
                    section: "REPORT MANAGEMENT",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "SALES & REVENUE",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Sales by Product", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers by Sales", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                    ],
                },
                {
                    section: "INVENTORY",
                    items: [
                        { name: "Inventory Overview", icon: <WarehouseIcon />, path: "/reports/inventory/overview" },
                    ],
                },
                {
                    section: "CUSTOMERS",
                    items: [
                        { name: "Customer Analytics", icon: <PeopleIcon />, path: "/reports/customers/analytics" },
                    ],
                },
                {
                    section: "PURCHASES & VENDORS",
                    items: [
                        { name: "Purchase Overview", icon: <ShoppingCartIcon />, path: "/reports/purchases/overview" },
                    ],
                },
                {
                    section: "FINANCIAL",
                    items: [
                        { name: "Profit & Loss", icon: <AccountBalanceIcon />, path: "/reports/financial/profit-loss" },
                        { name: "Profit by Product", icon: <ShowChartIcon />, path: "/reports/financial/profit-by-product" },
                    ],
                },
               
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
            manager: [
                {
                    section: "DASHBOARD",
                    items: [
                        { name: "Dashboard", icon: <DashboardIcon />, path: "/reports/dashboard" },
                    ],
                },
                {
                    section: "SALES & REVENUE",
                    items: [
                        { name: "Sales Overview", icon: <TrendingUpIcon />, path: "/reports/sales/overview" },
                        { name: "Sales by Product", icon: <Inventory2Icon />, path: "/reports/sales/by-product" },
                        { name: "Top Customers by Sales", icon: <PeopleIcon />, path: "/reports/sales/top-customers" },
                    ],
                },
                {
                    section: "INVENTORY",
                    items: [
                        { name: "Inventory Overview", icon: <WarehouseIcon />, path: "/reports/inventory/overview" },
                    ],
                },
                {
                    section: "CUSTOMERS",
                    items: [
                        { name: "Customer Analytics", icon: <PeopleIcon />, path: "/reports/customers/analytics" },
                    ],
                },
                {
                    section: "PURCHASES & VENDORS",
                    items: [
                        { name: "Purchase Overview", icon: <ShoppingCartIcon />, path: "/reports/purchases/overview" },
                    ],
                },
                {
                    section: "FINANCIAL",
                    items: [
                        { name: "Profit & Loss", icon: <AccountBalanceIcon />, path: "/reports/financial/profit-loss" },
                        { name: "Profit by Product", icon: <ShowChartIcon />, path: "/reports/financial/profit-by-product" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
        "HVMS": {
            admin: [
                {
                    section: "VENDOR MANAGEMENT",
                    items: [
                        { name: "Vendors", icon: <StorefrontIcon />, path: "/Vendors" },
                        { name: "Add Vendors", icon: <StorefrontIcon />, path: "/addVendor" },
                    ],
                },
                {
                    section: "SETTINGS",
                    items: [
                        { name: "Data Import/Export", icon: <ImportExportIcon />, path: "/configuration/data-wizard" },
                    ],
                },
                {
                    section: "ACCOUNT",
                    items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
                },
            ],
        },
    };

    const appMenus = menuItems[appKey];
    if (!appMenus) {
        return [];
    }

    if (isAdmin && appMenus.admin) return appMenus.admin;
    if (isManager && appMenus.manager) return appMenus.manager;
    if (isAgent && appMenus.agent) return appMenus.agent;

    return appMenus.admin || [];
};